import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueSafeTeleport from 'vue-safe-teleport'
import { Quasar, Notify, Dialog } from 'quasar'

import App from '@/App.vue'
import auth0Plugin from '@/setups/authSetup'
import router from './router'

import * as Sentry from '@sentry/vue'

import '@quasar/extras/material-icons/material-icons.css'
import 'quasar/src/css/index.sass'
import axios from 'axios'
import tracker from './tracker'
import LogRocket from 'logrocket'

const commit = {
  hash: import.meta.env.VITE_COMMIT_HASH || 'local-dev',
  timestamp: import.meta.env.VITE_COMMIT_TIMESTAMP || 'no-commit-timestamp'
}

console.log({
  'Commit Hash:': commit.hash,
  'Commit Timestamp:': commit.timestamp
})

const app = createApp(App)
app.use(auth0Plugin)

auth0Plugin
  .getAccessTokenSilently()
  .then(() => {
    app.use(VueSafeTeleport)
    app.use(router)
    app.use(tracker)
    app.use(createPinia())

    console.log('Environment:', import.meta.env.MODE)

    if (import.meta.env.MODE === 'production' && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [Sentry.browserTracingIntegration({ router })],
        // Tracing
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [/^https:\/\/api.hellodata\.ai/],
        release: 'frontend@' + commit.hash,
        environment: import.meta.env.MODE,
        beforeSend: (event) => {
          try {
            if (LogRocket) {
              Sentry.setContext('logrocket', {
                url: LogRocket.sessionURL
              })
            }
          } catch (error) {
            // TODO: remove the try/catch once we're sure the whole timing works well
            console.error('Error setting LogRocket context:', error)
          }
          return event
        }
      })
    }

    const preferDarkMode = localStorage.getItem('prefer-dark')
    let dark = 'auto' as 'auto' | boolean
    if (preferDarkMode !== null) {
      dark = preferDarkMode === 'true'
    }
    app.use(Quasar, {
      plugins: { Notify, Dialog },
      config: {
        dark
      }
    })

    const loginRedirectTo = localStorage.getItem('loginRedirectTo')
    if (loginRedirectTo !== null) {
      localStorage.removeItem('loginRedirectTo')
      router.push(loginRedirectTo)
    }

    app.mount('#app')
  })
  .catch(() => {
    localStorage.setItem('loginRedirectTo', window.location.pathname)
    auth0Plugin.loginWithRedirect()
  })

// add an interceptor to axios to detect 429 errors (rate limiting)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      Notify.create({
        message: error.response?.data.message ?? 'Forbidden',
        type: 'negative'
      })
    } else if (error.response?.status === 429) {
      Notify.create({
        message: 'Too many requests, please try again in a minute',
        type: 'negative'
      })
    } else if (error.response?.status === 500) {
      Notify.create({
        message:
          'An error occurred, please try again later, if the problem persists, please contact support',
        type: 'negative'
      })
    }
    return Promise.reject(error)
  }
)
